import { useState } from 'react';
import type { ReactNode } from 'react';
import { Copy, Check } from 'lucide-react';
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';

function CopyErrorButton({ title, description }: { title?: ReactNode; description?: ReactNode }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const parts: string[] = [];
    if (title && typeof title === 'string') parts.push(title);
    else if (title) parts.push(String(title));
    if (description && typeof description === 'string') parts.push(description);
    else if (description) parts.push(String(description));

    try {
      await navigator.clipboard.writeText(parts.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard not available
    }
  };

  return (
    <button
      onClick={handleCopy}
      title={copied ? 'Copiato!' : 'Copia errore'}
      className="shrink-0 rounded-md p-1 text-red-300 opacity-70 transition-all hover:opacity-100 hover:text-red-50 focus:outline-none focus:ring-2 focus:ring-red-400"
    >
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
    </button>
  );
}

export function Toaster() {
  const { toasts, dismiss } = useToast();

  return (
    <ToastProvider duration={2000}>
      {toasts.map(function ({ id, title, description, action, ...props }) {
        const isDestructive = props.variant === 'destructive';
        return (
          <Toast key={id} {...props} onClick={() => dismiss(id)} className={`${props.className ?? ''} cursor-pointer`}>
            <div className="grid gap-1 flex-1 min-w-0">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && (
                <ToastDescription>{description}</ToastDescription>
              )}
            </div>
            {action}
            {isDestructive && (
              <CopyErrorButton title={title} description={description} />
            )}
            <ToastClose />
          </Toast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}
